import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HiOutlineUserGroup, HiTrash } from 'react-icons/hi';
import { FaArrowLeft, FaPencil, FaUserPlus } from 'react-icons/fa6';
import { FaSave, FaSort, FaSortUp, FaSortDown, FaAddressBook } from 'react-icons/fa';
import {
  TbUsers,
  TbCreditCard,
  TbExternalLink,
  TbLink,
  TbShieldCheck,
  TbShieldOff,
  TbUnlink,
  TbUserMinus,
} from 'react-icons/tb';
import { AiOutlineClose } from 'react-icons/ai';
import { toast } from 'react-toastify';
import ApiImage from '../../components/misc/ApiImage';
import Community, { Admin } from '../../../types/community';
import Button from '../../components/buttons/Button';
import Input from '../../components/misc/Input';
import CommunityImageInput from '../../components/forms/CommunityImageInput';
import IconBadge from '../../components/icons/IconBadge';
import communityService from '../../../services/community/communityService';
import businessService from '../../../services/business/businessService';
import useSelectedBusiness, {
  getSelectedBusiness,
} from '../../../hooks/business/useSelectedBusiness';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import CommunityInviteLinkModal from '../../components/modals/CommunityInviteLinkModal';
import AddCommunityMemberModal from '../../components/modals/AddCommunityMemberModal';
import AddExternalMemberToCommunityModal from '../../components/modals/AddExternalMemberToCommunityModal';
import localContactService from '../../../services/community/localContactService';
import ChangeSubCommunitySubscriptionModal from '../../components/modals/ChangeSubCommunitySubscriptionModal';
import DeleteCommunityModal from '../../components/modals/DeleteCommunityModal';
import httpService from '../../../services/httpService';
import { CommunityInvite } from '../../../models/inviteService.model';
import { COMMUNITY_URL } from '../../../constants';
import membersService from '../../../services/community/adminService';
import Pagination from '../../components/misc/Pagination';
import { ActionMenu, ActionMenuButton } from '../../components/menus/ActionMenu';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableSpacerRow,
} from '../../components/tables/BaseTable';

/** Returns true when a Connection matches an Admin entry from the API. */
function isMemberAdmin(member: Connection, admins: Admin[]): boolean {
  return admins.some((a) => a.memberId != null && String(a.memberId) === String(member.memberId));
}

export default function CommunityDetailsPage(): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const communityIdFromUrl = Number(searchParams.get('id')) || undefined;
  const [community, setCommunity] = useState<Community | undefined>(location.state?.community);

  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(community?.name || '');
  const [profileImage, setProfileImage] = useState<string | undefined>(
    community?.picture || undefined,
  );
  const [profileImageAction, setProfileImageAction] = useState<'UPLOAD' | 'DELETE'>();
  const [loading, setLoading] = useState(false);
  const [membersLoading, setMembersLoading] = useState(true);

  const [members, setMembers] = useState<Connection[]>([]);
  const [communityAdmins, setCommunityAdmins] = useState<Admin[]>([]);

  // Table: sorting
  type SortField = 'name' | 'role' | 'verificationLabel';
  type SortDir = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPageIndex(0);
  };

  // Table: pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState<10 | 20 | 30 | 40 | 50>(50);

  const sortedMembers = useMemo(() => {
    const copy = [...members];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') {
        const na = `${a.fields.FIRST_NAME} ${a.fields.LAST_NAME}`;
        const nb = `${b.fields.FIRST_NAME} ${b.fields.LAST_NAME}`;
        cmp = na.localeCompare(nb);
      } else if (sortField === 'role') {
        const ra = isMemberAdmin(a, communityAdmins) ? 'admin' : 'member';
        const rb = isMemberAdmin(b, communityAdmins) ? 'admin' : 'member';
        cmp = ra.localeCompare(rb);
      } else if (sortField === 'verificationLabel') {
        cmp = (a.verificationLabel ?? '').localeCompare(b.verificationLabel ?? '');
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [members, communityAdmins, sortField, sortDir]);

  const pagedMembers = useMemo(
    () => sortedMembers.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize),
    [sortedMembers, pageIndex, pageSize],
  );

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);
  const [addExternalMemberModalOpen, setAddExternalMemberModalOpen] = useState(false);
  const [changeSubscriptionModalOpen, setChangeSubscriptionModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const selectedBusiness = useSelectedBusiness();

  useEffect(() => {
    const communityId = communityIdFromUrl ?? location.state?.community?.id;
    let cancelled = false;

    if (communityId && selectedBusiness?.businessId) {
      communityService
        .fetchCommunitiesForBusiness(selectedBusiness.businessId)
        .then((communities) => {
          if (cancelled) return;
          const found = communities.find((c) => c.id === communityId);
          if (!found) return;
          setCommunity(found);
          setName(found.name);
          setProfileImage(found.picture || undefined);
        })
        .catch((error) => {
          console.error('Failed to fetch community:', error);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [selectedBusiness?.businessId, communityIdFromUrl, location.state?.community?.id]);

  useEffect(() => {
    if (community?.name) {
      setName(community.name);
    }
    if (community?.picture) {
      setProfileImage(community.picture);
    }
  }, [community]);

  useEffect(() => {
    const fetchInviteLink = async () => {
      if (inviteModalOpen && community?.id) {
        try {
          const { data } = await httpService.get<CommunityInvite>(
            `/networks/communities/${community.id}/invite-link/`,
          );
          setCommunity({
            ...community,
            inviteFields: data,
          });
        } catch (error) {
          console.error('Failed to fetch invite link:', error);
          toast.error(t('toast.error.fetchInviteLink'));
        }
      }
    };

    fetchInviteLink();
  }, [inviteModalOpen, community?.id]);

  const fetchMembers = async () => {
    if (community?.id) {
      try {
        setMembersLoading(true);
        const [data, admins] = await Promise.all([
          communityService.getCommunityMembers(community.id),
          membersService.getAllAdmins(community.id),
        ]);
        setMembers(data);
        setCommunityAdmins(admins);
      } catch (error) {
        console.error('Failed to fetch members:', error);
        toast.error(t('toast.error.fetchMembers'));
      } finally {
        setMembersLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [community]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(t('toast.error.nameRequired'));
      return;
    }

    if (!community?.id) return;

    setLoading(true);
    try {
      const isBusinessCommunity = !!(
        selectedBusiness &&
        selectedBusiness.community &&
        selectedBusiness.community.id === community.id
      );

      const persistCommunityDetails = async (): Promise<void> => {
        const description = community.description || '';
        if (isBusinessCommunity && selectedBusiness?.businessId) {
          await businessService.updateBusiness(
            name,
            description,
            selectedBusiness.dashboard?.features || community.features || {},
          );
        } else if (selectedBusiness?.businessId) {
          await businessService.updateSubCommunity(selectedBusiness.businessId, community.id, {
            name,
            description,
          });
        } else {
          await communityService.updateCommunity(
            name,
            description,
            community.memberPermissions || {},
            community.features || {},
            community.id,
          );
        }
      };

      if (profileImageAction === 'UPLOAD' && profileImage) {
        await persistCommunityDetails();
        const file = await fetch(profileImage).then((res) => res.blob() as Promise<File>);
        if (isBusinessCommunity) {
          await businessService.setProfileImage(file);
          const b = getSelectedBusiness();
          setCommunity({
            ...community,
            name,
            picture: b?.picture || profileImage || community.picture || null,
          });
          setProfileImage(b?.picture || profileImage || community.picture || undefined);
        } else {
          await communityService.setProfileImage(file, community.id);
          setCommunity({ ...community, name, picture: profileImage || community.picture || null });
          setProfileImage(profileImage || community.picture || undefined);
        }
      } else if (profileImageAction === 'DELETE') {
        await persistCommunityDetails();
        if (isBusinessCommunity) {
          await businessService.deleteProfileImage();
          setCommunity({ ...community, name, picture: null });
          setProfileImage(undefined);
        } else {
          await communityService.deleteProfileImage(community.id);
          setCommunity({ ...community, name, picture: null });
          setProfileImage(undefined);
        }
      } else {
        await persistCommunityDetails();
        setCommunity({ ...community, name, picture: community.picture || null });
      }

      toast.success(t('toast.success.communityUpdated'));
      setIsEditing(false);
      setProfileImageAction(undefined);
    } catch (error) {
      console.error('Failed to update community:', error);
      toast.error(t('toast.error.updateCommunity'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setName(community?.name || '');
    setProfileImage(community?.picture || undefined);
    setProfileImageAction(undefined);
    setIsEditing(false);
  };

  const handleMakeAdmin = async (member: Connection) => {
    if (!community?.id || !member.memberId) return;
    try {
      await membersService.makeAdminById([Number(member.memberId)], community.id);
      const updatedAdmins = await membersService.getAllAdmins(community.id);
      setCommunityAdmins(updatedAdmins);
      toast.success(t('page.communityDetails.toast.makeAdminSuccess'));
    } catch (error) {
      console.error('Failed to make admin:', error);
      toast.error(t('page.communityDetails.toast.makeAdminError'));
    }
  };

  const handleRemoveAdmin = async (member: Connection) => {
    if (!community?.id || !member.memberId) return;
    try {
      await membersService.deleteAdminById([Number(member.memberId)], community.id);
      const updatedAdmins = await membersService.getAllAdmins(community.id);
      setCommunityAdmins(updatedAdmins);
      toast.success(t('page.communityDetails.toast.removeAdminSuccess'));
    } catch (error) {
      console.error('Failed to remove admin:', error);
      toast.error(t('page.communityDetails.toast.removeAdminError'));
    }
  };

  const handleRemoveMember = async (member: Connection) => {
    if (!community?.id) return;
    try {
      if (member.memberId) {
        await httpService.delete(`/networks/communities/${community.id}/members/${member.memberId}`);
      } else if (member.externalMemberId != null && community.dashboard?.dashboardId) {
        await localContactService.deleteExternalMemberFromDashboard(
          community.dashboard.dashboardId,
          member.externalMemberId,
        );
      } else {
        return;
      }
      setMembers((prev) => prev.filter((m) => m.dashboardMemberId !== member.dashboardMemberId));
      toast.success(
        member.parentExternalMemberId != null
          ? t('page.communityDetails.toast.unlinkMemberSuccess')
          : t('page.communityDetails.toast.removeMemberSuccess'),
      );
    } catch (error) {
      console.error('Failed to remove member:', error);
      toast.error(t('page.communityDetails.toast.removeMemberError'));
    }
  };

  const handleOpenInCommunityApp = (): void => {
    if (!community?.id) return;
    const url = `${COMMUNITY_URL}/c/${community.id}`;
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!community) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-gray-600">{t('page.communityDetails.noCommunity')}</p>
      </div>
    );
  }

  const isMainBusinessCommunity = !!(
    selectedBusiness?.community && selectedBusiness.community.id === community.id
  );

  const handleDeleteCommunity = async (): Promise<void> => {
    if (!selectedBusiness?.businessId) return;
    await businessService.deleteSubCommunity(selectedBusiness.businessId, community.id);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Back Button */}
      <div className="flex items-center gap-3">
        <Button
          onClick={() => navigate('/my-communities')}
          className="flex items-center gap-2 rounded-full p-3"
          variant="tertiary">
          <FaArrowLeft className="size-5" />
          <span>{t('general.back')}</span>
        </Button>
      </div>

      {/* Community Info Card */}
      <div className="flex h-fit w-full flex-col gap-4 rounded-[12px] bg-secondary-200/50 p-4">
        <div className="flex items-center justify-between">
          <h1 className="text-[28px] leading-[28px]">{community.name}</h1>
          <div className="flex items-center gap-4">
            {/* Open in Community app */}
            <Button
              variant="secondary"
              onClick={handleOpenInCommunityApp}
              className="flex-shrink-0 rounded-full p-3"
              title={t('page.communityDetails.openInCommunity')}>
              <TbExternalLink className="size-5" />
              {t('page.communityDetails.openInCommunity')}
            </Button>

            {!isEditing && (
              <Button
                className="flex-shrink-0 rounded-full p-3"
                variant="primary"
                onClick={() => setIsEditing(true)}
                title={t('page.communityDetails.editCommunity')}>
                <FaPencil className="size-5" />
              </Button>
            )}
            {!isEditing && !isMainBusinessCommunity && (
              <Button
                className="flex-shrink-0 rounded-full p-3"
                variant="danger-v2"
                onClick={() => setDeleteModalOpen(true)}
                title={t('page.communityDetails.deleteCommunity')}>
                <HiTrash className="size-5" />
              </Button>
            )}
          </div>
        </div>
        <div className="w-full border-b border-primary-300" />

        <div className="flex w-full flex-col gap-4">
          {isEditing ? (
            <div className="flex flex-col gap-4">
              <CommunityImageInput
                image={profileImage}
                className="h-[100px] w-[100px]"
                setImage={setProfileImage}
                setImageAction={setProfileImageAction}
                uploadOnSelect={false}
              />
              <Input
                id="community-name"
                label={t('page.communityDetails.name')}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-lg border px-3 py-2"
              />
              <div className="flex gap-3">
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={loading}
                  className="flex items-center gap-2 rounded-full p-3">
                  <FaSave className="size-5" />
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCancel}
                  disabled={loading}
                  className="rounded-full p-3">
                  <AiOutlineClose className="size-5" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {/* Community Picture */}
              {profileImage && (
                <ApiImage
                  path={profileImage}
                  className="h-[100px] w-[100px] rounded-[12px] object-cover"
                />
              )}

              {/* Community Name */}
              <div className="flex items-center gap-2">
                <IconBadge icon={HiOutlineUserGroup} className="rounded-[6px]" />
                <p className="text-sm">{community.name}</p>
              </div>

              {/* Members Count */}
              <div className="flex items-center gap-2">
                <IconBadge icon={TbUsers} className="rounded-[6px]" />
                <p className="text-sm">
                  {members.length}{' '}
                  {members.length === 1
                    ? t('page.communityDetails.members.one')
                    : t('page.communityDetails.members.other')}
                </p>
              </div>

              {/* Subscription */}
              <div className="flex items-center gap-2">
                <IconBadge icon={TbCreditCard} className="rounded-[6px]" />
                <p className="text-sm">
                  {t('page.subscriptions.activeSubscription')}:{' '}
                  <span className="font-semibold">
                    {community.subscription?.type
                      ? t(`page.subscriptions.${community.subscription.type}.title`)
                      : t('page.subscriptions.community_free.title')}
                  </span>
                </p>
                <Button
                  variant="secondary"
                  onClick={() => setChangeSubscriptionModalOpen(true)}
                  className="ml-2 rounded-full px-3 py-1 text-xs">
                  {t('page.communityDetails.changeSubscription')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Members Section */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">{t('page.communityDetails.members.other')}</h2>
            <p className="text-sm text-gray-600">
              {members.length} {t('general.total')}
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={() => setInviteModalOpen(true)}
              className="rounded-full px-4 py-2">
              <FaUserPlus className="size-5" />
              <span>{t('component.modal.addConnection.invite')}</span>
            </Button>
            {!isMainBusinessCommunity && community.dashboard?.dashboardId && (
              <Button
                variant="secondary"
                onClick={() => setAddExternalMemberModalOpen(true)}
                className="rounded-full px-4 py-2">
                <FaAddressBook className="size-5" />
                <span>{t('page.communityDetails.addExternalMembers')}</span>
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => setAddMemberModalOpen(true)}
              className="rounded-full px-4 py-2">
              <FaUserPlus className="size-5 text-secondary-50" />
              <span className="text-secondary-50">{t('page.communityDetails.addMembers')}</span>
            </Button>
          </div>
        </div>

        {membersLoading ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-gray-600">{t('general.loading')}</p>
          </div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <TbUsers className="mb-4 h-16 w-16 text-gray-400" />
            <p className="text-gray-600">{t('page.communityDetails.noMembers')}</p>
          </div>
        ) : (
          <BaseTable>
            <BaseTableHeader>
              <BaseTableHeaderRow>
                <SortableTh
                  label={t('page.communityDetails.table.name')}
                  field="name"
                  active={sortField === 'name'}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <SortableTh
                  label={t('page.communityDetails.table.role')}
                  field="role"
                  active={sortField === 'role'}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <SortableTh
                  label={t('page.communityDetails.table.verificationLabel')}
                  field="verificationLabel"
                  active={sortField === 'verificationLabel'}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <th className="text-right" />
              </BaseTableHeaderRow>
            </BaseTableHeader>
            <BaseTableBody>
              <BaseTableSpacerRow />
              {pagedMembers.map((member) => (
                <MemberRow
                  key={member.dashboardMemberId}
                  member={member}
                  isAdmin={isMemberAdmin(member, communityAdmins)}
                  onMakeAdmin={handleMakeAdmin}
                  onRemoveAdmin={handleRemoveAdmin}
                  onRemoveMember={handleRemoveMember}
                />
              ))}
            </BaseTableBody>
            <tfoot>
              <tr>
                <td colSpan={4}>
                  <Pagination
                    className="justify-start"
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    setPageIndex={setPageIndex}
                    setPageSize={setPageSize}
                    length={sortedMembers.length}
                  />
                </td>
              </tr>
            </tfoot>
          </BaseTable>
        )}
      </div>

      <CommunityInviteLinkModal
        open={inviteModalOpen}
        setOpen={setInviteModalOpen}
        community={community}
      />

      <AddCommunityMemberModal
        open={addMemberModalOpen}
        setOpen={setAddMemberModalOpen}
        communityId={community.id}
        mainCommunityId={isMainBusinessCommunity ? undefined : selectedBusiness?.community?.id}
        existingMemberUserIds={members.flatMap((m) => (m.userId != null ? [String(m.userId)] : []))}
        onMembersAdded={fetchMembers}
      />

      {!isMainBusinessCommunity && community.dashboard?.dashboardId && (
        <AddExternalMemberToCommunityModal
          open={addExternalMemberModalOpen}
          setOpen={setAddExternalMemberModalOpen}
          communityDashboardId={community.dashboard.dashboardId}
          existingMembers={members}
          onMembersAdded={fetchMembers}
        />
      )}

      {selectedBusiness && (
        <ChangeSubCommunitySubscriptionModal
          open={changeSubscriptionModalOpen}
          setOpen={setChangeSubscriptionModalOpen}
          community={community}
          businessId={selectedBusiness.businessId}
          onSubscriptionChanged={(newType) => {
            setCommunity((prev) =>
              prev
                ? {
                    ...prev,
                    paidByBusiness: newType !== 'COMMUNITY_FREE',
                    subscription: prev.subscription
                      ? {
                          ...prev.subscription,
                          type: newType,
                          active: newType !== 'COMMUNITY_FREE',
                        }
                      : newType !== 'COMMUNITY_FREE'
                        ? {
                            subscriptionId: '',
                            productId: '',
                            active: true,
                            willRenew: true,
                            type: newType,
                          }
                        : undefined,
                  }
                : prev,
            );
          }}
        />
      )}

      <DeleteCommunityModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        community={community}
        onDelete={handleDeleteCommunity}
        redirectTo="/my-communities"
      />
    </div>
  );
}

interface SortableThProps {
  label: string;
  field: 'name' | 'role' | 'verificationLabel';
  active: boolean;
  dir: 'asc' | 'desc';
  onSort: (f: 'name' | 'role' | 'verificationLabel') => void;
}

function SortableTh({ label, field, active, dir, onSort }: SortableThProps) {
  return (
    <th className="cursor-pointer select-none text-left" onClick={() => onSort(field)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          dir === 'asc' ? (
            <FaSortUp className="text-primary-600 h-3.5 w-3.5" />
          ) : (
            <FaSortDown className="text-primary-600 h-3.5 w-3.5" />
          )
        ) : (
          <FaSort className="h-3.5 w-3.5 opacity-40" />
        )}
      </span>
    </th>
  );
}

interface MemberRowProps {
  member: Connection;
  isAdmin: boolean;
  onMakeAdmin: (member: Connection) => void;
  onRemoveAdmin: (member: Connection) => void;
  onRemoveMember: (member: Connection) => void;
}

function MemberRow({
  member,
  isAdmin,
  onMakeAdmin,
  onRemoveAdmin,
  onRemoveMember,
}: MemberRowProps): JSX.Element {
  const { t } = useTranslation();

  const firstName = member.fields.FIRST_NAME ?? '';
  const lastName = member.fields.LAST_NAME ?? '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || '-';

  return (
    <BaseTableDataRow className="group transition-colors hover:bg-secondary-50">
      <td className="py-3.5">
        <div className="flex items-center gap-3">
          {member.picture ? (
            <ApiImage
              path={member.picture}
              alt={fullName}
              className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="text-primary-700 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-secondary-200 text-xs font-semibold">
              {firstName.charAt(0)}
              {lastName.charAt(0)}
            </div>
          )}
          <span className="font-medium text-gray-900">{fullName}</span>
        </div>
      </td>

      <td className="py-3.5">
        {isAdmin ? (
          <span className="text-primary-800 inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold">
            <TbShieldCheck className="h-3.5 w-3.5" />
            {t('page.communityDetails.table.roleAdmin')}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-secondary-200 px-2.5 py-0.5 text-xs font-medium text-gray-600">
            {t('page.communityDetails.table.roleMember')}
          </span>
        )}
      </td>

      <td className="py-3.5 text-sm text-gray-700">
        <span className="inline-flex items-center gap-2">
          {member.type === ConnectionType.MEMBER
            ? t('page.dashboard.table.columnLabel.registered')
            : member.type === ConnectionType.EXTERNAL_MEMBER && member.inviteSent
              ? t('page.dashboard.table.columnLabel.invited')
              : t('page.dashboard.table.columnLabel.notInvited')}
          {member.parentExternalMemberId != null && (
            <span
              className="text-primary-800 inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium"
              title={t('page.communityDetails.table.linkedFromMainTooltip')}>
              <TbLink className="h-3.5 w-3.5" />
              {t('page.communityDetails.table.linkedFromMain')}
            </span>
          )}
        </span>
      </td>

      <td className="py-3.5 pr-4">
        <div className="flex justify-end">
          <ActionMenu>
            {member.type === ConnectionType.MEMBER &&
              (isAdmin ? (
                <ActionMenuButton
                  icon={TbShieldOff}
                  text={t('page.communityDetails.table.removeAdmin')}
                  onClick={() => onRemoveAdmin(member)}
                />
              ) : (
                <ActionMenuButton
                  icon={TbShieldCheck}
                  text={t('page.communityDetails.table.makeAdmin')}
                  onClick={() => onMakeAdmin(member)}
                />
              ))}
            {member.parentExternalMemberId != null ? (
              <ActionMenuButton
                icon={TbUnlink}
                text={t('page.communityDetails.table.unlinkMember')}
                onClick={() => onRemoveMember(member)}
              />
            ) : (
              <ActionMenuButton
                icon={TbUserMinus}
                className="text-danger"
                text={t('page.communityDetails.table.removeMember')}
                onClick={() => onRemoveMember(member)}
              />
            )}
          </ActionMenu>
        </div>
      </td>
    </BaseTableDataRow>
  );
}
